/**
 * Mail — prototype `vMail()`, contract §11, infra C.1.
 *
 * Real server-side folders now (Task 6): Inbox and Sent are each their own
 * `mail.get_emails?folder=` fetch rather than a client-side `select` over one whole-mailbox
 * request, and read/flag/delete genuinely persist. Deleted and Drafts are DERIVED on the client
 * (`useMail.ts`'s `folderRows`): the server answers every other folder name with the Inbox, and
 * a deleted mail stays in its list with `status: Archived` — measured 2026-09-16, filed as D38.
 *
 * FE/BE split: the client owns folder selection, search text and which attachment is being
 * previewed. Everything else — read state, flags, folder moves, extraction — is the server's,
 * because it is shared with anyone else looking at the same mailbox.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PageHead, Placeholder } from '../../components/ui'
import { InboxPipelineStrip } from './InboxPipelineStrip'
import { useEnquiries } from '../../lib/api/useEnquiries'
import { mailEnquiryIndex } from '../../lib/mailProcessed'
import { useToast } from '../../components/Toast'
import { useSession } from '../../components/SessionProvider'
import {
  useDeleteMail,
  useFlagMail,
  useMailbox,
  useMailMessage,
  useMarkRead,
  useSendMail,
  toWireFolder,
  MAIL_POLL_MS,
} from '../../lib/api/useMail'
import { MAIL_PAGE_SIZE } from '../../lib/mailPaging'
import { folderTitle, htmlToText, involvesAddress, mailParticipants, type FolderSelection } from '../../lib/mail'
import { useDebounced } from '../../lib/useDebounced'
import { shouldPrewarmOnSelect } from '../../lib/mailPrewarm'
import { prewarmExtraction } from '../../lib/api/useMailExtraction'
import { useAutoEnquiries, usePortList, useVesselList } from '../../lib/api/useAutoEnquiries'
import { useUsers } from '../../lib/api/useUsers'
import { useEnquiryTypes } from '../../lib/api/useEnquiryTypes'
import { mailKind, type MailKind } from '../../lib/mailKinds'
import { mailScopeFor } from '../../lib/mailScope'
import { useAutoCreatedEnquiries } from '../../lib/api/useAutoEnquiries'
import { useServerAutoEnquiry } from '../../lib/api/serverAutoEnquiry'
import { useCurrentUser, useDeliveryLocationPriceLists, useDeliveryLocations, useOwnCompanies } from '../../lib/api/useMasters'
import { useSessionCompany } from '../../lib/api/company'
import { useQueryClient } from '@tanstack/react-query'
import { FolderRail } from './FolderRail'
import { MessageList } from './MessageList'
import { ReaderPane } from './ReaderPane'
import { AttachmentPreviewModal } from './AttachmentPreview'
import { ComposeModal, type ComposeSeed } from './ComposeModal'
import type { ComposePayload, MailAttachment, MailFolderSummary, MailMessage } from '../../lib/types'

/** `account: null` — there is no honest customer/supplier split to select by default; see
 * `FolderRail`'s module header (the account switcher was removed, not disabled — defect B4). */
const DEFAULT_FOLDER: FolderSelection = { folder: 'inbox', account: null }

/** The four folders `mail.get_emails` is confirmed to serve (see `useMail.ts`'s `WIRE_FOLDER`). */
type RailFolderKey = 'inbox' | 'sent' | 'drafts' | 'deleted'

const RAIL_FOLDERS: { key: RailFolderKey; title: string }[] = [
  { key: 'inbox', title: 'Inbox' },
  { key: 'sent', title: 'Sent' },
  { key: 'drafts', title: 'Drafts' },
  { key: 'deleted', title: 'Deleted' },
]

/** Real folders now page server-side, but the screen still shows one page at a time — this is
 * "big enough that a normal folder never truncates" rather than a paging control. */
/* The server caps a page at 100 rows whatever is asked for (measured 2026-09-25), so this IS the
   page size; the list grows by fetching the next offset — see `lib/mailPaging.ts`. */
const FOLDER_LIMIT = MAIL_PAGE_SIZE

export function MailPage() {
  const navigate = useNavigate()
  const toast = useToast()

  // Deep link from the RFQ screen's source card: ?mail=<id>&account=<customer|supplier>.
  const [params, setParams] = useSearchParams()
  const deepLink = params.get('mail')

  const [selection, setSelection] = useState<FolderSelection>(DEFAULT_FOLDER)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounced(search, 250)
  /**
   * The "Addressed to" filter — `null` is everyone. Client state, like folder and search: it is a
   * view preference, not a permission (`lib/mail.ts`'s `involvesAddress`, and Q24 for what real
   * per-user scoping would take).
   */
  const [addressedTo, setAddressedTo] = useState<string | null>(null)
  /*
    The kind chips (Akhil, 2026-09-19): to create · enquiry created · quoted · supplier replies ·
    RFQ sent · other. Derived per mail from what the list already knows (`mailKind`); counts are
    over the folder as searched and addressed, so a chip says how many it would show.
  */
  const [kindFilter, setKindFilter] = useState<MailKind | null>(null)
  const { myAddress, role } = useSession()
  /*
    WHOSE mail (Akhil, 2026-09-21): a purchaser sees only the threads they are on and cannot
    change that; a purchase manager sees everyone's, with the filter, seeded from the mailbox
    setting. `mailScopeFor` is the rule; `effectiveAddressedTo` is what the list is filtered by.
  */
  const me = useCurrentUser()
  const scope = mailScopeFor(role, me.data?.email ?? '', myAddress || null)
  useEffect(() => {
    if (!scope.locked && myAddress) setAddressedTo(myAddress)
  }, [myAddress, scope.locked])
  const effectiveAddressedTo = scope.locked ? scope.addressedTo : addressedTo
  const [selectedId, setSelectedId] = useState<string | null>(deepLink)
  const [preview, setPreview] = useState<MailAttachment | null>(null)
  const [compose, setCompose] = useState<ComposeSeed | null>(null)

  useEffect(() => {
    if (!deepLink) return
    const next = new URLSearchParams(params)
    next.delete('mail')
    next.delete('account')
    setParams(next, { replace: true })
  }, [deepLink, params, setParams])

  /*
    ONE real-row fetch (limit 500) — for whichever folder is currently open — plus three CHEAP
    `limit=1` probes for the rest, read only for their `total`. This used to be four unconditional
    500-row fetches purely to badge the rail, which is 4x the request volume of the old single
    whole-mailbox fetch and only gets worse as a mailbox fills (code review, Task 6 fix round 1).

    `limit=1`, not `limit=0`: nothing in the verified live shape says `get_emails` treats 0 as
    "no rows" rather than "no limit" (which would silently re-create the 4x-data problem), and 1
    is cheap enough that the difference from 0 doesn't matter.

    The `unread` badge trade-off this buys: a real per-folder unread COUNT would need every row
    (`get_emails` has no unread-count aggregate), so the Inbox badge only reports a true unread
    count while Inbox is the folder actually open — otherwise it shows `total`, same as every
    other folder. See `folders` below.

    Hooks still fire in the same order every render (rules-of-hooks) — only the LIMIT argument
    changes per folder, which is a normal query-key change, not a conditional hook call.
  */
  /*
    Which mails have already become enquiries. The enquiry list is the source (see
    `lib/mailProcessed.ts`) — the same call the pipeline strip makes, so React Query serves both
    from one request.

    Declared HERE, above this component's loading/error early returns: a hook added further down
    would be the fourth hook-order crash this codebase has had.
  */
  // Polled like the inbox: the server-side watcher (2026-09-21) creates enquiries with no
  // page open, so the badge on a mail has to catch up without a reload.
  const enquiriesForMail = useEnquiries({ limit: 200 }, { refetchInterval: MAIL_POLL_MS })
  const processedByMail = useMemo(
    () => mailEnquiryIndex(enquiriesForMail.data?.data ?? []),
    [enquiriesForMail.data],
  )

  const inboxQuery = useMailbox('inbox', selection.folder === 'inbox' ? FOLDER_LIMIT : 1)
  const sentQuery = useMailbox('sent', selection.folder === 'sent' ? FOLDER_LIMIT : 1)
  const draftsQuery = useMailbox('drafts', selection.folder === 'drafts' ? FOLDER_LIMIT : 1)
  const deletedQuery = useMailbox('deleted', selection.folder === 'deleted' ? FOLDER_LIMIT : 1)
  const folderQueries = { inbox: inboxQuery, sent: sentQuery, drafts: draftsQuery, deleted: deletedQuery } as const

  /*
    AUTOMATIC ENQUIRIES (Akhil, 2026-09-18): a new RFQ-looking mail arriving while this page is
    open is read and, when it resolves customer, delivery location and company on its own,
    becomes an enquiry without a click — the ✓ badge appears on the row and the board gains a
    card. Anything it cannot resolve is left for the create bar, and the toast says why. See
    `lib/autoEnquiry.ts` for the rules; the list poll is what brings the new mail here.
  */
  const ownCompanies = useOwnCompanies()
  const locationMaster = useDeliveryLocations()
  const priceLists = useDeliveryLocationPriceLists()
  const sessionCompany = useSessionCompany()
  // The purchaser comes from the mail's Cc (2026-09-21) — matched against the purchaser users.
  const purchaserUsers = useUsers('purchaser')
  const vessels = useVesselList()
  const ports = usePortList()
  const enquiryTypes = useEnquiryTypes()
  const autoContext = useMemo(
    () =>
      ownCompanies.data && locationMaster.data && priceLists.data && purchaserUsers.data && vessels.data && ports.data
        ? {
            ownCompanies: ownCompanies.data,
            locations: locationMaster.data,
            company: sessionCompany ?? '',
            priceLists: priceLists.data,
            purchasers: purchaserUsers.data.map((u) => u.email),
            vessels: vessels.data,
            ports: ports.data,
          }
        : null,
    [ownCompanies.data, locationMaster.data, priceLists.data, sessionCompany, purchaserUsers.data, vessels.data, ports.data],
  )
  // The Waves proxy may run this pipeline itself (2026-09-21); then the browser stands down.
  const serverWatches = useServerAutoEnquiry()
  useAutoEnquiries({
    mails: inboxQuery.data?.data ?? [],
    loaded: inboxQuery.isSuccess,
    processedByMail,
    context: autoContext,
    enquiryTypes: enquiryTypes.data ?? [],
    // Only while the inbox itself is listed in full — at `limit: 1` (another folder showing) a
    // new mail is not even on the list, so nothing is missed, only deferred to the next visit.
    enabled: selection.folder === 'inbox' && !serverWatches,
    onEvent: ({ mail, enquiryId, skipped, error }) => {
      if (enquiryId) toast(`Enquiry ${enquiryId} created automatically from “${mail.subject}”${error ? ` — ${error}` : ''}`)
      else if (skipped) toast(`“${mail.subject}” looks like an enquiry but was not created automatically — ${skipped}. Open it to create it.`)
      else if (error) toast(`Could not create an enquiry from “${mail.subject}”: ${error}`)
    },
  })

  const folders: MailFolderSummary[] = useMemo(
    () =>
      RAIL_FOLDERS.map(({ key, title }) => {
        const query = folderQueries[key]
        const isActiveFolder = selection.folder === key
        /*
          The badge is the SERVER's unread count, not anything derived from this request.

          It used to fall back to `total`, which for an unopened folder is the row count of a
          `limit: 1` fetch — so Sent, Drafts and Deleted all displayed "1" forever, a number that
          described our own request rather than the mailbox. `get_emails` returns `folder_counts`
          for exactly this, computed server-side and independent of the page size.

          `undefined` where the server sends no count for a folder: the rail then shows no badge
          at all, which is honest. A zero would claim "nothing unread" on a folder nobody counted.
        */
        const serverUnread = query.data?.folderCounts?.[toWireFolder(key)]?.unread
        // The open folder has every row in hand, so it can count them itself — and that is the
        // one number guaranteed to agree with what is on screen.
        const unread = isActiveFolder ? (query.data?.data.filter((m) => m.unread).length ?? 0) : serverUnread
        return { key, title, account: null, unread, total: query.data?.total ?? 0 }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inboxQuery.data, sentQuery.data, draftsQuery.data, deletedQuery.data, selection.folder],
  )

  const activeQuery = folderQueries[selection.folder in folderQueries ? (selection.folder as keyof typeof folderQueries) : 'inbox']
  const rawMessages = activeQuery.data?.data ?? []

  /**
   * "Addressed to" options, scoped to the folder currently open — NOT the whole mailbox any
   * more. The whole-mailbox version needed all four folders' rows loaded at once, which is
   * exactly the eager 4x fetch removed above; the other three folders now only carry a `total`,
   * not addresses to list. Switching folders resets which addresses are offered, which is a
   * fair trade for not re-fetching everything on every mount.
   */
  const participants = useMemo(() => mailParticipants(rawMessages), [rawMessages])
  const autoCreated = useAutoCreatedEnquiries()
  const kindOf = useMemo(() => {
    const map = new Map<string, MailKind>()
    for (const message of rawMessages) map.set(message.id, mailKind(message, processedByMail.get(message.id) ?? [], autoCreated.data ?? new Set()))
    return map
  }, [rawMessages, processedByMail, autoCreated.data])
  const messagesBeforeKind = useMemo<MailMessage[]>(() => {
    const term = debouncedSearch.trim().toLowerCase()
    return rawMessages.filter((message) => {
      // A locked scope whose address has not resolved shows nothing — never everything.
      if (scope.unresolved) return false
      if (effectiveAddressedTo !== null && !involvesAddress(message, effectiveAddressedTo)) return false
      if (term === '') return true
      return message.subject.toLowerCase().includes(term) || message.from.toLowerCase().includes(term)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawMessages, debouncedSearch, effectiveAddressedTo, scope.unresolved])
  const kindCounts = useMemo(() => {
    const counts = new Map<MailKind, number>()
    for (const message of messagesBeforeKind) {
      const k = kindOf.get(message.id) ?? 'other'
      counts.set(k, (counts.get(k) ?? 0) + 1)
    }
    return counts
  }, [messagesBeforeKind, kindOf])
  const messages = useMemo<MailMessage[]>(
    () => (kindFilter ? messagesBeforeKind.filter((m) => kindOf.get(m.id) === kindFilter) : messagesBeforeKind),
    [messagesBeforeKind, kindOf, kindFilter],
  )

  /** Keep a message selected as the folder or search changes — an empty reader beside a full
   * list looks broken. */
  useEffect(() => {
    if (activeQuery.isPending) return
    if (messages.length === 0) {
      setSelectedId(null)
      return
    }
    setSelectedId((current) => (current && messages.some((m) => m.id === current) ? current : messages[0].id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, activeQuery.isPending])

  const messageQuery = useMailMessage(selectedId)
  const mail = messageQuery.data ?? null

  const markRead = useMarkRead()
  // Opening a message marks it read server-side now (`mark_read` genuinely persists) — fired once
  // per opened id, from the real per-message `unread` `get_email` returns, not the list row's.
  useEffect(() => {
    if (mail && mail.unread) markRead.mutate({ id: mail.id, read: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mail?.id, mail?.unread])

  /**
   * Start the AI read as soon as a likely enquiry is SELECTED, so pressing "Create enquiry from
   * this mail" finds the answer already there instead of waiting on it.
   *
   * `startRun` (inside `prewarmExtraction`) makes a second call for the same mail a no-op, so
   * re-selecting, the 30s list poll, and a later explicit press all join the one run rather than
   * spending another LLM call.
   *
   * Gated by `shouldPrewarmOnSelect`, not by "a mail was opened": most inbox traffic is not an
   * enquiry, and a supplier's REPLY scores high on the RFQ classifier while being the one thing
   * we must not extract as a new enquiry. Waits for the full `get_email` body — the list row's
   * `body_html` is absent, and extracting from a preview line would produce a worse answer than
   * not pre-warming at all.
   */
  const queryClient = useQueryClient()
  useEffect(() => {
    if (!mail || !mail.body_html) return
    if (!shouldPrewarmOnSelect(mail)) return
    prewarmExtraction(mail, { queryClient })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mail?.id, mail?.body_html])

  const flag = useFlagMail({ onError: (e) => toast(e.message) })
  const remove = useDeleteMail({
    onSuccess: () => toast('Moved to Deleted'),
    onError: (e) => toast(e.message),
  })
  const send = useSendMail({
    onSuccess: () => {
      setCompose(null)
      toast('Mail sent')
    },
    onError: (e) => toast(`Could not send: ${e.message}`),
  })

  /* ---------- actions ---------- */

  const openCompose = (mode: ComposeSeed['mode']) => {
    if (mode === 'new') {
      setCompose({ mode, account: selection.account ?? 'customer' })
      return
    }
    if (!mail) return

    if (mode === 'draft') {
      // A draft re-opens as it was saved — recipients, attachments and line breaks intact.
      // Sending it now goes through the normal `send_email` call; there is no "update this draft
      // in place" method (see `useMail.ts`'s module header, "Dropped: … Save draft").
      setCompose({
        mode,
        account: mail.account,
        to: mail.to,
        cc: mail.cc,
        subject: mail.subject,
        body: htmlToText(mail.body_html),
        attachments: mail.attachments,
        sourceId: mail.id,
      })
      return
    }

    const quoted = `\n\n---------- ${mode === 'forward' ? 'Forwarded' : 'Original'} message ----------\n${htmlToText(mail.body_html)}`
    setCompose({
      mode,
      account: mail.account,
      to: mode === 'reply' || mode === 'replyall' ? [mail.from] : [],
      cc: mode === 'replyall' ? mail.to.concat(mail.cc) : [],
      subject: `${mode === 'forward' ? 'FW: ' : 'RE: '}${mail.subject.replace(/^(RE|FW):\s*/i, '')}`,
      body: quoted,
      attachments: mode === 'forward' ? mail.attachments : [],
      sourceId: mail.id,
    })
  }

  const handleSend = (payload: ComposePayload) => send.mutate(payload)

  /* ---------- states ---------- */

  if (inboxQuery.isPending) return <Placeholder>Loading mail…</Placeholder>

  if (inboxQuery.error && !inboxQuery.data) {
    return (
      <Placeholder>
        <b>Could not load the mailboxes.</b> Check the mock backend is running (<b>npm run dev:all</b>).
        <div style={{ marginTop: 6 }}>{(inboxQuery.error as Error).message}</div>
      </Placeholder>
    )
  }

  return (
    <>
      <PageHead help="mail" title="Mail" />

      {/* Point 29 — what is in flight, without leaving the inbox. Holds its own query; see the
          component's header for why it is not a hook up here. */}
      <InboxPipelineStrip />

      <div className="mailwrap">
        <FolderRail
          folders={folders}
          selected={selection}
          onSelect={(next) => {
            setSelection(next)
            setSearch('')
            if (!scope.locked) setAddressedTo(myAddress || null)
            setSelectedId(null)
          }}
          onCompose={() => openCompose('new')}
        />

        <MessageList
          title={folderTitle(selection)}
          folder={selection.folder}
          messages={messages}
          selectedId={selectedId}
          search={search}
          isPending={activeQuery.isPending}
          onReachEnd={() => {
            // The next page, as the bottom of the list comes into view. React Query ignores this
            // while a fetch is in flight, so a fast scroll cannot stack requests.
            if (activeQuery.hasNextPage && !activeQuery.isFetchingNextPage) void activeQuery.fetchNextPage()
          }}
          loadingMore={activeQuery.isFetchingNextPage}
          hasMore={Boolean(activeQuery.hasNextPage)}
          participants={participants}
          addressedTo={effectiveAddressedTo}
          processedByMail={processedByMail}
          kindFilter={kindFilter}
          kindCounts={kindCounts}
          onKindFilter={setKindFilter}
          kindOf={kindOf}
          onSearch={setSearch}
          onAddressedTo={setAddressedTo}
          addressLocked={scope.locked}
          onSelect={setSelectedId}
          // `flag_email` needs an explicit boolean and there is no field to read the current
          // state back from (see `useFlagMail`'s doc comment) — every click means "flag it".
          onToggleFlag={(id) => flag.mutate({ id, flagged: true })}
        />

        <ReaderPane
          alreadyProcessed={selectedId ? (processedByMail.get(selectedId) ?? []) : []}
          mail={mail}
          folder={selection.folder}
          isPending={messageQuery.isPending && !!selectedId}
          onReply={() => openCompose('reply')}
          onReplyAll={() => openCompose('replyall')}
          onForward={() => openCompose('forward')}
          onEditDraft={() => openCompose('draft')}
          onToggleFlag={() => mail && flag.mutate({ id: mail.id, flagged: true })}
          onDelete={() => mail && remove.mutate(mail.id)}
          onOpenEnquiry={(enquiryId) => navigate(`/rfq/${enquiryId}`)}
          onPreviewAttachment={setPreview}
        />
      </div>

      {preview && mail ? (
        <AttachmentPreviewModal
          attachment={preview}
          mail={mail}
          onClose={() => setPreview(null)}
          // The one call site where opening an attachment is a genuine "is this a new enquiry?"
          // signal worth an LLM call — see the prop's own doc comment in AttachmentPreview.tsx.
          prewarm
        />
      ) : null}

      {compose ? (
        <ComposeModal
          seed={compose}
          sending={send.isPending}
          onSend={handleSend}
          onClose={() => setCompose(null)}
        />
      ) : null}
    </>
  )
}
